import { Alert } from '@agentnet/components';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Table, TableBody, TableCell, TableHead, TableRow, Theme, Tooltip } from '@material-ui/core';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import { mismatchDLText, mismatchPassportText } from 'features/help/constants';
import React from 'react';
import { IdentityField } from '../utils/getIdentityFields';

interface Props {
  fields: IdentityField[];
  isPassport: boolean;
  isDL: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      tableLayout: 'auto',
      width: '100%',
      marginBottom: theme.spacing(3),
      borderCollapse: 'collapse',
      '& .MuiTableCell-root:first-child': {
        paddingLeft: theme.spacing(1),
        maxWidth: '119px',
      },
    },
    headerCell: {
      fontWeight: 'bold',
      color: theme.palette.common.black,
      fontSize: pxToRem(13),
      height: '28px !important',
      padding: `${theme.spacing(1)}px !important`,
    },
    fieldCell: {
      backgroundColor: theme.palette.brand.grey,
      color: theme.palette.common.black,
      minHeight: '100%',
      fontWeight: 500,
    },
    valueCell: {
      padding: theme.spacing(0.75, 1),
      verticalAlign: 'top',
      borderRight: `1px solid ${theme.palette.divider}`,
      borderBottom: `1px solid ${theme.palette.divider}`,
      wordBreak: 'break-word',
    },
    mismatchRow: {
      backgroundColor: theme.palette.agGrid.overrideBackground,
    },
    naText: {
      fontWeight: 400,
      fontSize: 9,
    },
    mismatchField: {
      color: theme.palette.warning.main,
      fontSize: pxToRem(18),
    },
    flexField: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    mismatchIcon: {
      fontSize: pxToRem(18),
    },
    alert: {
      padding: theme.spacing(0, 2),
    },
  }),
);

const IdentityInfoTable: React.FC<Props> = ({ fields, isPassport, isDL }) => {
  const classes = useStyles();
  const frontHeader = isPassport ? 'Front of Passport / Other' : 'Front of ID';
  const backHeader = isPassport ? 'MRZ Results' : 'Back of ID Barcode Result';
  const hasMismatch = fields.some((field) => field.isMismatch);
  return (
    <>
      <Table className={classes.table} size="small">
        <TableHead>
          <TableRow>
            <TableCell className={clsx(classes.valueCell, classes.headerCell)}>
              <strong>Field</strong>
            </TableCell>
            <TableCell className={clsx(classes.valueCell, classes.headerCell)}>
              <strong>{frontHeader}</strong>
            </TableCell>
            <TableCell className={clsx(classes.valueCell, classes.headerCell)}>
              <strong>{backHeader}</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field, idx) => (
            <TableRow key={idx} className={field.isMismatch ? classes.mismatchRow : ''}>
              <TableCell
                className={clsx(classes.fieldCell, classes.valueCell, {
                  [classes.mismatchRow]: field.isMismatch,
                })}
              >
                <div className={classes.flexField}>
                  {field.label}
                  {field.isMismatch && (
                    <Tooltip title="Mismatch detected">
                      <ReportProblemIcon className={classes.mismatchField} />
                    </Tooltip>
                  )}
                </div>
              </TableCell>
              <TableCell className={classes.valueCell}>
                {field.frontValue === 'N/A' ? <span className={classes.naText}>N/A</span> : field.frontValue}
              </TableCell>
              <TableCell className={classes.valueCell}>
                {field.backValue === 'N/A' ? <span className={classes.naText}>N/A</span> : field.backValue}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {hasMismatch && (
        <Alert
          severity="warning"
          variant="standard"
          icon={<ReportProblemIcon className={classes.mismatchIcon} />}
          className={classes.alert}
        >
          {isPassport ? mismatchPassportText : mismatchDLText}
        </Alert>
      )}
    </>
  );
};

export default IdentityInfoTable;
